import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { usePlatform } from '../store';
import { PRIVACY_EVENT, privacyHidden } from '../privacy';

// Only navigation paths and coarse acquisition data are sent; query strings and form values are excluded.
export function TrafficTracker() {
  const { pathname, search } = useLocation();
  const { data } = usePlatform();
  const initialized = data !== null;
  const initial = useRef(true);
  const pagePath =
    pathname === '/login' && new URLSearchParams(search).get('mode') === 'register'
      ? '/login?mode=register'
      : pathname;
  const preview = new URLSearchParams(search).get('preview') === 'true';
  useEffect(() => {
    if (!initialized || pathname.startsWith('/admin') || preview) return;
    const id = crypto.randomUUID();
    let referrer = '';
    if (initial.current && document.referrer) {
      try {
        referrer = new URL(document.referrer).origin;
      } catch {
        /* Unknown source. */
      }
    }
    let cancelled = false,
      registered = false,
      sequence = 0;
    let visibleMs = 0,
      lastTick = performance.now();
    let visible = document.visibilityState === 'visible' && !privacyHidden();
    let ended: 'navigation' | 'pagehide' | undefined;
    const beat = (active: boolean, keepalive = false, endReason?: 'navigation' | 'pagehide') => {
      const tick = performance.now();
      if (visible) visibleMs += Math.max(0, tick - lastTick);
      lastTick = tick;
      visible = active;
      ended = endReason;
      if (!registered) return;
      void fetch('/api/traffic/heartbeat', {
        method: 'POST',
        credentials: 'include',
        keepalive,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id,
          active,
          sequence: ++sequence,
          visibleMs: Math.floor(visibleMs),
          endReason,
        }),
      }).catch(() => {});
    };
    void fetch('/api/traffic/pageview', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id,
        path: pagePath,
        timingVersion: 1,
        active: document.visibilityState === 'visible' && !privacyHidden(),
        referrer,
        source: initial.current
          ? (new URLSearchParams(search).get('utm_source') || '').slice(0, 100)
          : '',
      }),
    })
      .then((response) => {
        registered = response.ok && response.status !== 204;
        if (cancelled || ended) beat(false, true, ended || 'navigation');
      })
      .catch(() => {});
    initial.current = false;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible' && !privacyHidden()) beat(true);
    }, 30000);
    const visibility = () => beat(document.visibilityState === 'visible' && !privacyHidden(), true);
    const hide = () => beat(false, true, 'pagehide');
    const show = visibility;
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener(PRIVACY_EVENT, visibility);
    window.addEventListener('pagehide', hide);
    window.addEventListener('pageshow', show);
    return () => {
      cancelled = true;
      clearInterval(interval);
      beat(false, true, 'navigation');
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener(PRIVACY_EVENT, visibility);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('pageshow', show);
    };
  }, [pagePath, preview, initialized]);
  return null;
}
